const STORAGE_PREFIX = 'hashely:board-viewport:';

export function boardViewportStorageKey(personName, hashtag) {
  if (!personName || !hashtag) return null;
  return `${STORAGE_PREFIX}${personName}:${hashtag}`;
}

export function normalizeViewport(viewport) {
  if (!viewport || typeof viewport !== 'object') return null;
  const x = Number(viewport.x);
  const y = Number(viewport.y);
  const zoom = Number(viewport.zoom);
  if (![x, y, zoom].every(Number.isFinite)) return null;
  return { x, y, zoom };
}

/** Quantize camera values so tiny float drift does not trigger persistence. */
export function quantizeViewport(viewport) {
  const normalized = normalizeViewport(viewport);
  if (!normalized) return null;
  return {
    x: Math.round(normalized.x * 10) / 10,
    y: Math.round(normalized.y * 10) / 10,
    zoom: Math.round(normalized.zoom * 1000) / 1000,
  };
}

export function viewportsEqual(a, b, { positionEpsilon = 0.5, zoomEpsilon = 0.001 } = {}) {
  const va = normalizeViewport(a);
  const vb = normalizeViewport(b);
  if (!va || !vb) return !va && !vb;
  return (
    Math.abs(va.x - vb.x) < positionEpsilon
    && Math.abs(va.y - vb.y) < positionEpsilon
    && Math.abs(va.zoom - vb.zoom) < zoomEpsilon
  );
}

export function readLocalBoardViewport(personName, hashtag) {
  const key = boardViewportStorageKey(personName, hashtag);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return normalizeViewport(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeLocalBoardViewport(personName, hashtag, viewport) {
  const key = boardViewportStorageKey(personName, hashtag);
  const next = normalizeViewport(viewport);
  if (!key || !next || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Prefer local (freshest on same device) when allowed; otherwise prefer the server board. */
export function resolveBoardViewport(board, personName, hashtag, { preferLocal = true } = {}) {
  const fromBoard = normalizeViewport(board?.viewport);
  const fromLocal = readLocalBoardViewport(personName, hashtag);
  if (preferLocal) return fromLocal || fromBoard || null;
  return fromBoard || fromLocal || null;
}
